/**
 * AI 이상행동 감시 훅 — 로그인/행위 이벤트 Worker 트리거 stub.
 *
 * 책임 경계:
 * - 이 파일은 **이벤트 전송 + 결과 수신** 만 담당한다.
 * - 실제 LLM 판정(이상 여부, Slack 알림, 2FA 재요구)은 Jupiter(#5 AI Assistant Layer) 의
 *   `/admin/ai/anomaly-assess` Worker 에서 수행. 이 모듈은 그 인터페이스만 정의한다.
 *
 * 호출 흐름 (docs/specs/superadmin.md §1.3):
 *   1) CF Access 인증 완료 → RequireAdmin 에서 emitLoginEvent(user_uuid)
 *   2) Worker 가 signal(JSON) 수신 → LLM 평가 → anomaly_log 적재
 *   3) 평가 결과(verdict) 리턴 → critical 이면 CF Access re-auth 유도 (Phase 4.5)
 *
 * migrations/0006_admin_audit_d1.sql — anomaly_log 스키마.
 */

import type { AnomalyAssessment, AnomalyEvent } from './types';

const ANOMALY_ENDPOINT = '/admin/ai/anomaly-assess';

/** Jupiter 가 구현을 주입할 때 override 가능한 훅. 기본은 fetch 기반. */
export type AnomalyAssessFn = (event: AnomalyEvent) => Promise<AnomalyAssessment | null>;

let anomalyAssessor: AnomalyAssessFn | null = null;

/**
 * Jupiter 가 커스텀 평가 함수를 주입한다.
 * - 테스트/로컬에서 mocking 에도 사용.
 * - null 로 리셋하면 기본 fetch 구현으로 복원.
 */
export function registerAnomalyAssessor(fn: AnomalyAssessFn | null): void {
  anomalyAssessor = fn;
}

function resolveApiBase(): string {
  const base = import.meta.env.VITE_ADMIN_API_URL ?? '';
  return base.replace(/\/+$/, '');
}

/** 브라우저 시그널을 수집해 AnomalyEvent 로 포장. */
export function collectLoginSignal(actor: string): AnomalyEvent {
  if (typeof window === 'undefined') {
    return {
      actor,
      eventType: 'login',
      signal: { runtime: 'ssr_or_worker' },
      occurredAt: new Date().toISOString(),
    };
  }

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return {
    actor,
    eventType: 'login',
    signal: {
      ua: navigator.userAgent,
      lang: navigator.language,
      tz,
      platform: navigator.platform,
      screen: `${window.screen.width}x${window.screen.height}`,
      referrer: document.referrer || null,
    },
    occurredAt: new Date().toISOString(),
  };
}

/**
 * 기본 평가자 — 단순히 Worker 로 이벤트를 POST 하고 JSON 응답 파싱.
 * Jupiter 는 이 시그너처를 유지한 상태로 실제 LLM 판정 로직을 endpoint 에서 구현.
 */
async function defaultAssess(event: AnomalyEvent): Promise<AnomalyAssessment | null> {
  const apiBase = resolveApiBase();
  if (!apiBase) return null;

  try {
    const response = await fetch(`${apiBase}${ANOMALY_ENDPOINT}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(event),
    });
    if (!response.ok) {
      console.warn('[Lashpic Admin] anomaly-assess 응답 오류:', response.status);
      return null;
    }
    const data = (await response.json()) as Partial<AnomalyAssessment>;
    if (!data.verdict) return null;
    return {
      verdict: data.verdict,
      reason: data.reason ?? '',
      actionTaken: data.actionTaken ?? 'none',
    };
  } catch (error) {
    console.error('[Lashpic Admin] anomaly-assess 네트워크 실패:', error);
    return null;
  }
}

/**
 * 로그인 이벤트 전송.
 * - 결과가 critical 이면 상위 레이어(RequireAdmin) 가 sign-out 유도.
 * - 네트워크 실패는 UI 를 블로킹하지 않음 (best-effort).
 */
export async function emitLoginEvent(actor: string): Promise<AnomalyAssessment | null> {
  const event = collectLoginSignal(actor);
  const assessor = anomalyAssessor ?? defaultAssess;
  return assessor(event);
}

/** 고위험 액션(Step-up) 이후 이벤트 전송. */
export async function emitActionEvent(
  actor: string,
  actionType: string,
  extra: Record<string, unknown> = {},
): Promise<AnomalyAssessment | null> {
  const event: AnomalyEvent = {
    actor,
    eventType: 'action',
    signal: { action_type: actionType, ...extra },
    occurredAt: new Date().toISOString(),
  };
  const assessor = anomalyAssessor ?? defaultAssess;
  return assessor(event);
}
